import { TestExtensionRootState, useTestExtensionSelector } from '@common/store/test-extenstion-store';
import { ExpandMore, Refresh } from '@mui/icons-material';
import { Timeline, TimelineConnector, TimelineContent, TimelineDot, TimelineItem, TimelineSeparator } from '@mui/lab';
import { Accordion, AccordionDetails, AccordionSummary, Box, Chip, CircularProgress, IconButton, Typography } from '@mui/material';
import moment from 'moment';
import * as React from 'react';
import { useCallback } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useParams } from 'react-router-dom';
import { DBSessionEventsThunks, sessionEventsSelectByQuery } from '../../../../common/reducers/DBServiceThunks';
import { useAppDispatch } from '../../../../common/reducers/hooks/store.hook';
import { SessionEvent as ImportedSessionEvent } from '../../../../common/models/session-event';
import { createSelector } from '@reduxjs/toolkit';

const ITEMS_PER_PAGE = 20;

const getSessionEventsState = (state: TestExtensionRootState) => state.DBSessionEventsReducer;

const selectPaginationStatus = createSelector(
  [getSessionEventsState],
  (state) => ({
    itemsLoading: state.itemsLoading,
    itemsLoaded: state.itemsLoaded,
    total: state.total,
    skip: state.skip,
    limit: state.limit
  })
);

// Define the type for events from the Redux store
// Extended to include all possible fields from API response
interface StoreSessionEvent extends Omit<ImportedSessionEvent, 'state'> {
  timestamp: string;
  event?: string;
  triggerdBy?: string;
  sessionType?: string;
  stream?: string;
  hasSynced?: boolean;
  qrcode?: string;
  state: string; // Use string type to allow for any state value
}

interface GroupedEvents {
  [key: string]: {
    latestEvent: StoreSessionEvent;
    events: StoreSessionEvent[];
  };
}

// Helper function to determine if state is a "connected" state
const isConnectedState = (state: string): boolean => {
  return state === 'connected' || state === 'CONNECTED';
};

// Helper function to determine if state is a "connecting" state
const isConnectingState = (state: string): boolean => {
  return state === 'connecting' || state === 'CONNECTING';
};

// Helper function to determine if state is a "disconnected" state
const isDisconnectedState = (state: string): boolean => {
  return state === 'disconnected' || state === 'DISCONNECTED' || state === 'UNPAIRED';
};

const getEventColor = (event: string): 'info' | 'secondary' | 'success' | 'primary' | 'warning' | 'error' | 'default' => {
  // Session creation events
  if (event.startsWith('session_')) {
    switch (event) {
      case 'session_request': return 'info';
      case 'session_exists': return 'secondary';
      case 'session_created': return 'success';
      case 'session_assigned': return 'primary';
      case 'session_cleanup': return 'warning';
      case 'session_removed': return 'error';
      case 'session_error': return 'error';
      default: return 'default';
    }
  }

  // Browser events
  if (event.startsWith('browser_')) {
    switch (event) {
      case 'browser_starting': return 'info';
      case 'browser_started': return 'success';
      case 'browser_closed': return 'warning';
      default: return 'default';
    }
  }

  // Navigation events
  if (event.startsWith('navigation_')) {
    switch (event) {
      case 'navigation_started': return 'info';
      case 'navigation_complete': return 'success';
      case 'navigation_failed': return 'error';
      default: return 'default';
    }
  }

  // WhatsApp events
  switch (event) {
    case 'qr_generated': return 'info';
    case 'qr_timeout': return 'warning';
    case 'authenticated': return 'success';
    case 'store_ready': return 'success';
    case 'connection_error': return 'error';
    case 'disconnected': return 'warning';
    case 'handleWaStateHealthCheck': return 'info';
    case 'appStateChanged': return 'warning';
    default: return 'default';
  }
};

const formatDetails = (details: any) => {
  if (!details) return null;

  // Remove type from details since we show it in title
  const { type, ...otherDetails } = details;

  const formatValue = (value: any): string => {
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value);
  };

  if (Object.keys(otherDetails).length === 0) return null;

  return (
    <Box sx={{ mt: 1 }}>
      {Object.entries(otherDetails).map(([key, value]) => (
        <Box key={key} sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
          <Chip
            label={key.replace(/_/g, ' ')}
            size="small"
            variant="outlined"
          />
          <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
            {formatValue(value)}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

export const SessionTimeline: React.FC = () => {
  const dispatch = useAppDispatch();
  const { sessionId } = useParams<{ sessionId: string }>();
  const [loading, setLoading] = React.useState(true);

  const events = useTestExtensionSelector((state) => {
    // Instead of filtering by sessionId, get all events
    return sessionEventsSelectByQuery(state.DBSessionEventsReducer, {}, null, { fromDateTime: -1 });
  }) as StoreSessionEvent[];

  const paginationStatus = useTestExtensionSelector(selectPaginationStatus);
  const { itemsLoading, total } = paginationStatus;

  const loadMore = useCallback(() => {
    if (itemsLoading) return;
    setLoading(true);
    console.log(`Loading more session events, skip: ${events.length}, limit: ${ITEMS_PER_PAGE}`);
    
    // Load all events without session ID filter
    dispatch(DBSessionEventsThunks.find({
      query: {},
      sort: { fromDateTime: -1 },
      skip: events.length,
      limit: ITEMS_PER_PAGE
    })).finally(() => setLoading(false));
  }, [dispatch, events.length, itemsLoading]);

  React.useEffect(() => {
    console.log(`Initial loading of session events`);
    setLoading(true);
    
    // Load all events without session ID filter
    dispatch(DBSessionEventsThunks.find({
      query: {},
      sort: { fromDateTime: -1 },
      skip: 0,
      limit: ITEMS_PER_PAGE
    })).finally(() => {
      console.log(`Finished loading session events, found ${events.length} events`);
      setLoading(false);
    });
  }, [dispatch]);

  const hasEvents = events.length > 0;
  
  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton onClick={() => loadMore()}>
          <Refresh />
        </IconButton>
        <Typography>
          {sessionId ? `Session ID: ${sessionId}` : 'All Sessions'}
        </Typography>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && !hasEvents && (
        <Box sx={{ textAlign: 'center', p: 4 }}>
          <Typography variant="h6">No session events found</Typography>
          <Typography color="textSecondary">
            There are no events available.
          </Typography>
        </Box>
      )}

      {hasEvents && (
        <InfiniteScroll
          dataLength={events.length}
          next={loadMore}
          hasMore={events.length < total}
          loader={<Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress /></Box>}
        >
          <Timeline>
            {events.map((event, index) => (
              <TimelineItem key={event._id}>
                <TimelineSeparator>
                  <TimelineDot
                    color={
                      isConnectedState(event.state) ? 'success' :
                      isConnectingState(event.state) ? 'warning' :
                      isDisconnectedState(event.state) ? 'error' :
                      'grey'
                    }
                  />
                  {index < events.length - 1 && <TimelineConnector />}
                </TimelineSeparator>
                <TimelineContent>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {/* Date and Time */}
                    <Typography variant="body1" fontWeight="bold">
                      {moment(event.fromDateTime).format('YYYY-MM-DD HH:mm:ss')}
                    </Typography>
                    
                    {/* Status and trigger information */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Chip
                        label={event.state}
                        color={
                          isConnectedState(event.state) ? 'success' :
                          isConnectingState(event.state) ? 'warning' :
                          isDisconnectedState(event.state) ? 'error' :
                          'default'
                        }
                        size="small"
                      />
                      
                      {(event.event || event.triggerdBy) && (
                        <Chip 
                          label={event.event || event.triggerdBy || 'Unknown Event'} 
                          size="small" 
                          color={getEventColor(event.event || event.triggerdBy || '')}
                        />
                      )}
                      
                      {event.sessionType && (
                        <Chip 
                          label={event.sessionType} 
                          size="small"
                          variant="outlined" 
                        />
                      )}
                      
                      <Typography variant="body2" color="textSecondary">
                        Session: {event.sessionId}
                      </Typography>
                    </Box>
                    
                    {/* Additional details */}
                    {(event.details || event.podId) && (
                      <Box sx={{ mt: 0.5 }}>
                        {event.details && formatDetails(event.details)}
                        {event.podId && (
                          <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                            <Chip
                              label="pod"
                              size="small"
                              variant="outlined"
                            />
                            <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                              {event.podId}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    )}
                  </Box>
                </TimelineContent>
              </TimelineItem>
            ))}
          </Timeline>
        </InfiniteScroll>
      )}
    </Box>
  );
}; 